import React, { useEffect, useState, useCallback, useMemo } from "react";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Card from "@mui/material/Card";
import axios from "axios";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import HOST_URL from "../../../utils/Url";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ToastApp from "../../../ToastApp";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import { usePermissionData } from "../../../hooks/usePermissionData";

interface DesignationData {
  designation_id: number;
  designation_name: string;
  short_name: string;
  dept_id: number;
  isActive: boolean;
  id: number;
  serialNo: number;
}

export default function DesignationMaster() {
  const [designations, setDesignations] = useState<DesignationData[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string>("");

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { permissionData, loading: permissionLoading } = usePermissionData();

  // Memoized fetch function
  const fetchDesignationsData = useCallback(async () => {
    try {
      setIsLoading(true);
      const collectData = {
        designation_id: -1,
      };

      const response = await axios.post(
        HOST_URL.HOST_URL2 + `Designation/GetDesignationmaster`,
        collectData
      );

      if (response.data.data) {
        const designationsWithIds = response.data.data.map((dept: any, index: number) => ({
          ...dept,
          serialNo: index + 1,
          id: dept.designation_id,
        }));
        setDesignations(designationsWithIds);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load designations");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchDesignationsData();
  }, [fetchDesignationsData]);

  // Setup columns based on permission data
  useEffect(() => {
    if (permissionLoading || designations.length === 0) return;

    const baseColumns: GridColDef[] = [
      {
        field: "serialNo",
        headerName: t("text.SrNo"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 60
      },
      {
        field: "designation_name",
        headerName: "Designation Name",
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 200
      },
      {
        field: "short_name",
        headerName: "Designation Code",
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 160
      },
    ];

    // Add actions column only if user has edit or delete permissions
    if (permissionData?.isEdit || permissionData?.isDel) {
      const actionsColumn: GridColDef = {
        field: "actions",
        headerClassName: "MuiDataGrid-colCell",
        headerName: t("text.Action"),
        width: 200,
        renderCell: (params) => (
          <Stack
            spacing={1}
            direction="row"
            sx={{ alignItems: "center", marginTop: "5px" }}
          >
            {permissionData?.isEdit && (
              <EditIcon
                style={{
                  fontSize: "20px",
                  color: "blue",
                  cursor: "pointer",
                }}
                className="cursor-pointer"
                onClick={() => routeChangeEdit(params.row)}
              />
            )}
            {permissionData?.isDel && (
              <DeleteIcon
                style={{
                  fontSize: "20px",
                  color: "red",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setDeleteId(params.row.id.toString());
                  confirmDialog({
                    message: "Do you want to delete this record?",
                    header: "Delete Confirmation",
                    icon: "pi pi-info-circle",
                    acceptClassName: "p-button-danger",
                    accept: () => handleDeleteAccept(params.row.id),
                    reject: handleDeleteReject,
                  });
                }}
              />
            )}
            {/* {permissionData?.isEdit && (
              <Switch
                checked={Boolean(params.row.isActive)}
                style={{
                  color: params.row.isActive ? "green" : "#FE0000",
                }}
                onChange={(event) => handleSwitchChange(event, params.row)}
                inputProps={{
                  "aria-label": "Toggle Switch",
                }}
              />
            )} */}
          </Stack>
        ),
      };

      setColumns([actionsColumn, ...baseColumns]);
    } else {
      setColumns(baseColumns);
    }
  }, [permissionData, permissionLoading, designations.length, t]);

  // Switch change handler
  const handleSwitchChange = useCallback((
    event: React.ChangeEvent<HTMLInputElement>,
    value: DesignationData
  ) => {
    const collectData = {
      designation_id: value.id,
      designation_name: value.designation_name,
      dept_id: value.dept_id,
      short_name: value.short_name,
      isActive: event.target.checked,
      user_ID: -1,
      sortOrder: 0,
    };

    axios
      .post(HOST_URL.HOST_URL + `Designation/AddUpdateDesignationmaster`, collectData)
      .then((response) => {
        if (response.data.isSuccess) {
          toast.success(response.data.mesg);
          fetchDesignationsData();
        } else {
          toast.error(response.data.mesg);
        }
      })
      .catch((error) => {
        console.error("Error updating designation status:", error);
        toast.error("Failed to update designation status");
      });
  }, [fetchDesignationsData]);

  // Navigation handlers
  const routeChangeEdit = (row: DesignationData) => {
    navigate(`/master/DesignationMasterEdit`, {
      state: row,
    });
  };

  const routeChangeAdd = () => {
    navigate(`/master/DesignationMasterAdd`);
  };

  // Delete handlers
  const handleDeleteAccept = useCallback(async (id: number) => {
    try {
      const collectData = {
        designation_id: id,
        user_ID: 0,
      };

      const response = await axios.post(
        HOST_URL.HOST_URL2 + `Designation/DeleteDesignationmaster`,
        collectData
      );

      if (response.data.isSuccess) {
        toast.success(response.data.mesg);
        fetchDesignationsData();
      } else {
        toast.error(response.data.mesg);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete designation");
    } finally {
      setDeleteId("");
    }
  }, [fetchDesignationsData]);

  const handleDeleteReject = useCallback(() => {
    toast.warn("Deletion cancelled", { autoClose: 3000 });
    setDeleteId("");
  }, []);

  // Memoized adjusted columns
  const adjustedColumns = useMemo(() =>
    columns.map(column => ({ ...column })),
    [columns]
  );

  // Loading state handling
  if (permissionLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card
        style={{
          width: "100%",
          backgroundColor: "#E9FDEE",
          border: ".5px solid #FF7722 ",
          marginTop: "3vh",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            "& .MuiDataGrid-colCell": {
              backgroundColor: `var(--grid-headerBackground)`,
              color: `var(--grid-headerColor)`,
              fontSize: 17,
              fontWeight: 900
            },
          }}
          style={{ padding: "10px" }}
        >
          <ConfirmDialog />

          <Typography
            gutterBottom
            variant="h5"
            component="div"
            sx={{ padding: "20px" }}
            align="left"
          >
            {t("text.desName")}
          </Typography>
          <Divider />

          <Box height={10} />

          {/* Add Button - Only show if user has add permission */}
          {permissionData?.isAdd && (
            <Stack direction="row" spacing={2} classes="my-2 mb-2">
              <Button
                onClick={routeChangeAdd}
                variant="contained"
                endIcon={<AddCircleIcon />}
                size="large"
                style={{ backgroundColor: `var(--header-background)` }}
              >
                {t("text.add")}
              </Button>
            </Stack>
          )}

          {/* Data Grid Section */}
          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <Box>
              <br />
              <div style={{ width: "100%", backgroundColor: `var(--grid-background)` }}>
                <DataGrid
                  rows={designations}
                  columns={adjustedColumns}
                  autoHeight
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  rowSpacingType="border"
                  pagination={true}
                  pageSizeOptions={[5, 10, 25, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                    },
                  }}
                  disableRowSelectionOnClick
                />
              </div>
            </Box>
          )}
        </Paper>
      </Card>
      <ToastApp />
    </>
  );
}


















// import * as React from "react";
// import Paper from "@mui/material/Paper";
// import { useEffect, useState } from "react";
// import {
//   Box,
//   Button,
//   Divider,
//   Stack,
//   TextField,
//   Typography,
// } from "@mui/material";
// import AddCircleIcon from "@mui/icons-material/AddCircle";
// import SearchIcon from "@mui/icons-material/Search";
// import Swal from "sweetalert2";
// import EditIcon from "@mui/icons-material/Edit";
// import DeleteIcon from "@mui/icons-material/Delete";
// import Card from "@mui/material/Card";
// import Grid from "@mui/material/Grid";
// import PrintIcon from "@mui/icons-material/Print";
// import axios from "axios";
// import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
// import HOST_URL from "../../../utils/Url";
// import { useNavigate, useLocation } from "react-router-dom";
// import { useTranslation } from "react-i18next";
// import { toast } from "react-toastify";
// import ToastApp from "../../../ToastApp";
// import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
// import Switch from "@mui/material/Switch";
// import Chip from "@mui/material/Chip";
// import CircularProgress from "@mui/material/CircularProgress";

// interface MenuPermission {
//   isAdd: boolean;
//   isEdit: boolean;
//   isPrint: boolean;
//   isDel: boolean;
// }

// export default function DesignationMaster() {
//   const [dept, setDept] = useState([]);
//   const [columns, setColumns] = useState<any>([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const location = useLocation();
//   const [permissionData, setPermissionData] = useState<MenuPermission>({
//     isAdd: false,
//     isEdit: false,
//     isPrint: false,
//     isDel: false,
//   });

//   let navigate = useNavigate();
//   const { t } = useTranslation();

//   useEffect(() => {
//     // const dataString = localStorage.getItem("userdata");
//     //   if (dataString) {
//     //     const data = JSON.parse(dataString);
//     //     if (data && data.length > 0) {
//     //       const userPermissionData = data[0]?.userPermission;
//     //       if (userPermissionData && userPermissionData.length > 0) {
//     //         const menudata = userPermissionData[0]?.parentMenu;
//     //         for (let index = 0; index < menudata.length; index++) {
//     //           const childMenudata = menudata[index]?.childMenu;
//     //           const pathrow = childMenudata.find(
//     //             (x: any) => x.path === location.pathname
//     //           );
//     //           console.log("data", pathrow);
//     //           if (pathrow) {
//     //             setPermissionData(pathrow);
//     //           }
//     //         }
//     //       }
//     //     }
//     //   }
//     fetchZonesData();
//   }, [isLoading]);

//   const handleSwitchChange = (
//     event: React.ChangeEvent<HTMLInputElement>,
//     value: any
//   ) => {
//     console.log(value);
//     const collectData = {
//       designation_id: value.id,
//       designation_name: value.designation_name,
//       dept_id: value.dept_id,
//       short_name: value.short_name,
//       isActive: event.target.checked,
//       user_ID: -1,
//       sortOrder: 0,
//     };
//     console.log("switch Data", collectData);
//     axios
//       .post(HOST_URL.HOST_URL + `Designation/AddUpdateDesignationmaster`, collectData)
//       .then((response) => {
//         if (response.data.isSuccess) {
//           toast.success(response.data.mesg);
//           fetchZonesData();
//         } else {
//           toast.error(response.data.mesg);
//         }
//       });
//   };
//   const routeChangeEdit = (row: any) => {
//     console.log("row " + row);

//     let path = `/master/DesignationMasterEdit`;
//     navigate(path, {
//       state: row,
//     });
//   };

//   const routeChangeAdd = () => {
//     let path = `/master/DesignationMasterAdd`;
//     navigate(path);
//   };

//   let delete_id = "";

//   const accept = () => {
//     const collectData = {
//       designation_id: delete_id,
//       user_ID: 0,
//     };
//     console.log("collectData " + JSON.stringify(collectData));
//     axios
//       .post(HOST_URL.HOST_URL2 + `Designation/DeleteDesignationmaster`,
//         collectData
//       )
//       .then((response) => {
//         if (response.data.isSuccess) {
//           toast.success(response.data.mesg);
//           fetchZonesData();
//         } else {
//           toast.error(response.data.mesg);
//         }
//       });
//   };

//   const reject = () => {
//     // toast.warn({summary: 'Rejected', detail: 'You have rejected', life: 3000 });
//     toast.warn("Rejected: You have rejected", { autoClose: 3000 });
//   };

//   const handledeleteClick = (del_id: any) => {
//     // console.log(del_id + " del_id ");
//     delete_id = del_id;
//     confirmDialog({
//       message: "Do you want to delete this record ?",
//       header: "Delete Confirmation",
//       icon: "pi pi-info-circle",
//       acceptClassName: "p=-button-danger",
//       accept,
//       reject,
//     });
//   };

//   const fetchZonesData = async () => {
//     try {
//       const collectData = {
//         designation_id: -1,
//       };
//       const response = await axios.post(
//         HOST_URL.HOST_URL2 + `Designation/GetDesignationmaster`,
//         collectData
//       );
//       const data = response.data.data;
//       const designationWithIds = data.map((dept: any, index: any) => ({
//         ...dept,
//         serialNo: index + 1,
//         id: dept.designation_id,
//       }));
//       setDept(designationWithIds);
//       setIsLoading(false);

//       if (data.length > 0) {
//         const columns: GridColDef[] = [
//           {
//             field: "actions",
//             headerClassName: "MuiDataGrid-colCell",
//             headerName: t("text.Action"),
//             width: 150,

//             renderCell: (params) => {
//               return [
//                 <Stack
//                   spacing={1}
//                   direction="row"
//                   sx={{ alignItems: "center", marginTop: "5px" }}
//                 >
//                   {/* {permissionData?.isEdit ? ( */}
//                   <EditIcon
//                     style={{
//                       fontSize: "20px",
//                       color: "blue",
//                       cursor: "pointer",
//                     }}
//                     className="cursor-pointer"
//                     onClick={() => routeChangeEdit(params.row)}
//                   />
//                   {/* // ) : ( */}
//                   {/* //   "" */}
//                   {/* // )} */}
//                   {/* // {permissionData?.isDel ? ( */}
//                   <DeleteIcon
//                     style={{
//                       fontSize: "20px",
//                       color: "red",
//                       cursor: "pointer",
//                     }}
//                     onClick={() => {
//                       handledeleteClick(params.row.id);
//                     }}
//                   />
//                   {/* ) : (
//                     ""
//                   )} */}
//                   {/* <Switch
//                     checked={Boolean(params.row.isActive)}
//                     style={{
//                       color: params.row.isActive ? "green" : "#FE0000",
//                     }}
//                     onChange={(value: any) =>
//                       handleSwitchChange(value, params.row)
//                     }
//                     inputProps={{
//                       "aria-label": "Toggle Switch",
//                     }}
//                   /> */}
//                 </Stack>,
//               ];
//             },
//           },

//           {
//             field: "serialNo",
//             headerName: t("text.SrNo"),
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 60
//           },
//           {
//             field: "designation_name",
//             headerName: "Designation Name",
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 200
//           },
//           {
//             field: "short_name",
//             headerName: "Designation Code",
//             flex: 1,
//             headerClassName: "MuiDataGrid-colCell",
//             minWidth: 160
//           },
//           // {
//           //   field: "isActive",
//           //   headerName: t("text.Status"),
//           //   flex: 1,
//           //   headerClassName: "MuiDataGrid-colCell",
//           //   renderCell: (params) => [
//           //     <Stack direction="row" spacing={1}>
//           //       {params.row.isActive ? (
//           //         <Chip
//           //           label={t("text.Active")}
//           //           color="success"
//           //           style={{ fontSize: "14px" }}
//           //         />
//           //       ) : (
//           //         <Chip
//           //           label={t("text.InActive")}
//           //           color="error"
//           //           style={{ fontSize: "14px" }}
//           //         />
//           //       )}
//           //     </Stack>,
//           //   ],
//           // },
//         ];
//         setColumns(columns as any);
//       }
//     } catch (error) {
//       console.error("Error fetching data:", error);
//       // setLoading(false);
//     }
//   };

//   const adjustedColumns = columns.map((column: any) => ({
//     ...column,
//   }));

//   return (
//     <>
//       <Card
//         style={{
//           width: "100%",
//           // height: "100%",
//           backgroundColor: "#E9FDEE",
//           border: ".5px solid #FF7722 ",
//           marginTop: "3vh",
//         }}
//       >
//         <Paper
//           sx={{
//             width: "100%",
//             overflow: "hidden",
//             "& .MuiDataGrid-colCell": {
//               backgroundColor: `var(--grid-headerBackground)`,
//               color: `var(--grid-headerColor)`,
//               fontSize: 17,
//               fontWeight: 900
//             },
//           }}
//           style={{ padding: "10px", }}
//         >
//           <ConfirmDialog />

//           <Typography
//             gutterBottom
//             variant="h5"
//             component="div"
//             sx={{ padding: "20px" }}
//             align="left"
//           >
//             {t("text.desName")}
//           </Typography>
//           <Divider />

//           <Box height={10} />

//           <Stack direction="row" spacing={2} classes="my-2 mb-2">
//             {/* {permissionData?.isAdd == true && ( */}
//             <Button
//               onClick={routeChangeAdd}
//               variant="contained"
//               endIcon={<AddCircleIcon />}
//               size="large"
//               style={{ backgroundColor: `var(--header-background)` }}
//             >
//               {t("text.add")}
//             </Button>
//             {/* // ) } */}

//             {/* {permissionData?.isPrint == true ? (
//               <Button variant="contained" endIcon={<PrintIcon />} size="large">
//                 {t("text.print")}
//               </Button>
//             ) : (
//               ""
//             )} */}
//           </Stack>

//           {isLoading ? (
//             <div
//               style={{
//                 display: "flex",
//                 justifyContent: "center",
//                 alignItems: "center",
//               }}
//             >
//               <CircularProgress />
//             </div>
//           ) : (
//             <Box>
//               <br />
//               <div style={{ width: "100%", backgroundColor: `var(--grid-background)` }}>
//                 <DataGrid
//                   rows={dept}
//                   columns={adjustedColumns}
//                   autoHeight
//                   slots={{
//                     toolbar: GridToolbar,
//                   }}
//                   rowSpacingType="border"
//                   pagination={true}
//                   pageSizeOptions={[5, 10, 25, 50, 100].map((size) => ({
//                     value: size,
//                     label: `${size}`,
//                   }))}
//                   initialState={{
//                     pagination: { paginationModel: { pageSize: 5 } },
//                   }}
//                   slotProps={{
//                     toolbar: {
//                       showQuickFilter: true,
//                     },
//                   }}
//                 />
//               </div>
//             </Box>
//           )}
//         </Paper>
//       </Card>
//       <ToastApp />
//     </>
//   );
// }
